import React from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, 
    Button, Box, Typography, TextField, Stack, IconButton, 
    Grid, Paper, ToggleButtonGroup, ToggleButton, Divider,
    useMediaQuery, useTheme
} from '@mui/material';
import { FaPalette, FaHistory, FaCode, FaEye, FaPlus } from 'react-icons/fa';
import { MdClose, MdSave } from 'react-icons/md';
import ReactQuill, { Quill } from 'react-quill-new';

// Configure Quill to be more permissive with HTML tags
// Configure Quill to be more permissive
// const Block = Quill.import('blots/block');
// Block.tagName = 'DIV'; 
// Quill.register(Block, true);

// Allow style, class, and align attributes on all blocks
const Parchment = Quill.import('parchment');
const StyleAttributor = new Parchment.StyleAttributor('style', 'style', {
    scope: Parchment.Scope.BLOCK
});
const InlineStyleAttributor = new Parchment.StyleAttributor('style', 'style', {
    scope: Parchment.Scope.INLINE
});
const ClassAttributor = new Parchment.ClassAttributor('class', 'class', {
    scope: Parchment.Scope.BLOCK
});
const AlignAttributor = new Parchment.StyleAttributor('align', 'text-align', {
    scope: Parchment.Scope.BLOCK
});

Quill.register(StyleAttributor, true);
Quill.register(InlineStyleAttributor, true);
Quill.register(ClassAttributor, true);
Quill.register(AlignAttributor, true);

const Link = Quill.import('formats/link');
class CustomLink extends Link {
    static sanitize(url) {
        return url; // Allow all protocol schemes
    }
}
Quill.register(CustomLink, true);

// Custom Button Blot
const Embed = Quill.import('blots/embed');
class ButtonBlot extends Embed {
    static create(value) {
        let node = super.create();
        node.setAttribute('href', value.url);
        node.setAttribute('target', '_blank');
        node.setAttribute('title', `Link: ${value.url}`); // Show URL on hover
        node.innerText = value.text;
        node.style.cssText = `
            background-color: ${value.color};
            color: #ffffff;
            padding: 12px 24px;
            text-decoration: none;
            border-radius: 6px;
            font-weight: bold;
            display: inline-block;
            font-family: sans-serif;
            text-align: center;
            margin: 10px 0;
            line-height: 1;
            cursor: pointer;
        `;
        return node;
    }

    static value(node) {
        return {
            url: node.getAttribute('href'),
            text: node.innerText,
            color: node.style.backgroundColor
        };
    }
}
ButtonBlot.blotName = 'action-button';
ButtonBlot.tagName = 'a';
ButtonBlot.className = 'email-btn'; // Optional class for external selection
Quill.register(ButtonBlot, true);

const EmailTemplateEditor = ({
    show,
    onHide,
    selectedTemplate,
    setSelectedTemplate,
    emailBgColor,
    setEmailBgColor,
    emailAccentColor,
    setEmailAccentColor,
    handleSaveTemplate
}) => {
    const theme = useTheme();
    const quillRef = React.useRef(null);
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const [activeView, setActiveView] = React.useState('editor'); // 'editor' | 'preview'
    const [editTab, setEditTab] = React.useState('content'); // 'content' (Visual), 'structure' (HTML), 'style' (Colors)
    
    // Separate local states for the two main fields
    const [localBody, setLocalBody] = React.useState(selectedTemplate?.bodyContent || '');
    const [localFrame, setLocalFrame] = React.useState(selectedTemplate?.htmlContent || '');

    // Initial load and reset view whenever the component mounts (forced by key) 
    // or when the selectedTemplate object itself changes while open
    React.useEffect(() => {
        if (show && selectedTemplate) {
            setLocalBody(selectedTemplate.bodyContent || '');
            setLocalFrame(selectedTemplate.htmlContent || '');
            // We don't reset view/tab here to allow switching between content/style 
            // without losing the current tab, but we DO ensure content is correct.
        }
    }, [show, selectedTemplate]);

    // Function to render preview by merging frame and content
    const getLocalRenderedTemplate = () => {
        let frame = localFrame || '';
        let body = localBody || '';

        const replacements = {
            '{{content}}': body,
            '{{message}}': '<i>(Text aus dem Kontaktformular)</i>',
            '{{senderName}}': 'Max Mustermann',
            '{{playerName}}': 'Max',
            '{{senderEmail}}': 'vorschau@tc-opfingen.de',
            '{{email}}': 'vorschau@tc-opfingen.de',
            '{{topicLabel}}': 'Beispiel Thema',
            '{{loginLink}}': '#',
            '{{resetLink}}': '#',
            '{{temporaryPassword}}': 'Geheim123!',
            '{{backgroundColor}}': emailBgColor,
            '{{emailBackgroundColor}}': emailBgColor,
            '{{accentColor}}': emailAccentColor,
            '{{emailAccentColor}}': emailAccentColor,
            '{{subject}}': selectedTemplate?.subject || '(Kein Betreff)'
        };
        
        let rendered = frame;
        Object.keys(replacements).forEach(key => {
            rendered = rendered.split(key).join(replacements[key]);
        });
        return rendered;
    };

    const insertTag = (tag) => {
        const placeholder = `{{${tag}}}`;
        if (editTab === 'content') {
            const quill = quillRef.current.getEditor();
            const range = quill.getSelection(true);
            quill.insertText(range.index, placeholder);
            quill.setSelection(range.index + placeholder.length);
        } else if (editTab === 'structure') {
            setLocalFrame(prev => prev + placeholder);
        }
    };

    const insertActionButton = () => {
        if (editTab !== 'content') return;
        const key = selectedTemplate?.key;
        const linkTag = key === 'password-reset' ? '{{resetLink}}' : '{{loginLink}}';
        const defaultLabel = key === 'password-reset' ? "Passwort jetzt zurücksetzen" : "Jetzt Passwort festlegen";
        
        const label = prompt("Button-Text eingeben:", defaultLabel) || "Klick hier";
        
        const quill = quillRef.current.getEditor();
        const range = quill.getSelection(true);
        
        quill.insertEmbed(range.index, 'action-button', {
            text: label,
            url: linkTag,
            color: emailAccentColor
        });
        quill.setSelection(range.index + 1); 
    };

    const getAvailableTags = () => {
        const key = selectedTemplate?.key;
        if (key === 'password-reset') {
            return ['playerName', 'resetLink', 'emailAccentColor'];
        }
        if (key === 'new-member') {
            return ['playerName', 'email', 'loginLink', 'emailAccentColor'];
        }
        if (key === 'contact-form') {
            return ['senderName', 'senderEmail', 'topicLabel', 'message', 'emailAccentColor'];
        }
        return ['playerName', 'email', 'emailAccentColor'];
    };

    const onSave = () => {
        // Build the final template object to send back
        const updated = {
            ...selectedTemplate,
            htmlContent: localFrame,
            bodyContent: localBody,
            backgroundColor: emailBgColor,
            accentColor: emailAccentColor
        };
        handleSaveTemplate(updated);
    };

    return (
        <Dialog 
            open={show} 
            onClose={onHide} 
            fullScreen={isMobile}
            maxWidth="xl"
            fullWidth
            PaperProps={{ sx: { minHeight: isMobile ? '100vh' : '85vh' } }}
            disableEnforceFocus
        >
            <DialogTitle sx={{ 
                bgcolor: 'primary.main', color: 'white', px: 3, py: 1.5,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FaPalette />
                    <Typography variant="h6" fontWeight="bold">E-Mail Design</Typography>
                </Box>
                <IconButton onClick={onHide} sx={{ color: 'white' }} size="small">
                    <MdClose />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {/* Mobile View Toggle */}
                {isMobile && (
                    <Box sx={{ borderBottom: '1px solid #ddd' }}>
                        <ToggleButtonGroup
                            value={activeView}
                            exclusive
                            onChange={(_, next) => next && setActiveView(next)}
                            fullWidth
                            size="small"
                        >
                            <ToggleButton value="editor" sx={{ borderRadius: 0, border: 'none' }}>Editor</ToggleButton>
                            <ToggleButton value="preview" sx={{ borderRadius: 0, border: 'none' }}>Vorschau</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                )}

                <Grid container sx={{ flexGrow: 1, height: isMobile ? 'calc(100vh - 150px)' : 'calc(85vh - 64px)' }}>
                    {/* Editor Side */}
                    <Grid item xs={12} lg={6} sx={{ 
                        borderRight: { lg: '1px solid #ddd' }, 
                        display: (isMobile && activeView !== 'editor') ? 'none' : 'flex', 
                        flexDirection: 'column',
                        bgcolor: 'background.paper',
                        overflowY: 'auto',
                        height: '100%'
                    }}>
                        {/* Metadata Section */}
                        <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Template Name"
                                        fullWidth
                                        size="small"
                                        value={selectedTemplate?.name || ''}
                                        onChange={e => setSelectedTemplate({...selectedTemplate, name: e.target.value})}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Template Key"
                                        fullWidth
                                        size="small"
                                        value={selectedTemplate?.key || ''}
                                        disabled={selectedTemplate?.isSystem}
                                        onChange={e => setSelectedTemplate({...selectedTemplate, key: e.target.value})}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="E-Mail Betreff"
                                        fullWidth
                                        size="small"
                                        value={selectedTemplate?.subject || ''}
                                        onChange={e => setSelectedTemplate({...selectedTemplate, subject: e.target.value})}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Tab Switcher */}
                        <Box sx={{ borderBottom: '1px solid #eee' }}>
                            <ToggleButtonGroup
                                value={editTab}
                                exclusive
                                onChange={(_, next) => next && setEditTab(next)}
                                fullWidth
                                size="small"
                                sx={{ borderRadius: 0 }}
                            >
                                <ToggleButton value="content" sx={{ py: 1.5, gap: 1 }}>
                                    <FaEye /> Text-Inhalt
                                </ToggleButton>
                                <ToggleButton value="structure" sx={{ py: 1.5, gap: 1, borderLeft: '1px solid #eee !important' }}>
                                    <FaCode /> Design-Rahmen
                                </ToggleButton>
                                <ToggleButton value="style" sx={{ py: 1.5, gap: 1, borderLeft: '1px solid #eee !important' }}>
                                    <FaPalette /> Farben
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        {/* TAB: CONTENT (Visual Editor) */}
                        {editTab === 'content' && (
                            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ p: 1.5, bgcolor: '#fdf7e3', borderBottom: '1px solid #ffe0b2', display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                    <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 'bold', mr: 1 }}>HILFE:</Typography>
                                    {getAvailableTags().map(tag => (
                                        <Button 
                                            key={tag} 
                                            size="small" 
                                            variant="outlined" 
                                            color="warning"
                                            onClick={() => insertTag(tag)}
                                            sx={{ py: 0, px: 1, textTransform: 'none', fontSize: '0.7rem', height: 22 }}
                                        >
                                            {`{${tag}}`}
                                        </Button>
                                    ))}
                                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                                    {selectedTemplate?.key !== 'contact-form' && (
                                        <Button 
                                            size="small" 
                                            variant="contained" 
                                            color="warning" 
                                            startIcon={<FaPlus />}
                                            onClick={insertActionButton}
                                            sx={{ py: 0, px: 1, textTransform: 'none', fontSize: '0.7rem', height: 22 }}
                                        >
                                            Button einfügen
                                        </Button>
                                    )}
                                </Box>
                                <Box sx={{ p: 2, flexGrow: 1, overflowY: 'auto' }}>
                                    <ReactQuill 
                                        ref={quillRef}
                                        theme="snow" 
                                        value={localBody} 
                                        onChange={setLocalBody}
                                        modules={{
                                            toolbar: [
                                                [{ 'header': [1, 2, 3, false] }],
                                                ['bold', 'italic', 'underline', 'strike'],
                                                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                                [{ 'align': [] }],
                                                ['link'],
                                                ['clean']
                                            ],
                                        }}
                                        style={{ height: isMobile ? '300px' : '400px', marginBottom: '40px' }}
                                    />
                                </Box>
                            </Box>
                        )}

                        {/* TAB: STRUCTURE (HTML Editor) */}
                        {editTab === 'structure' && (
                            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ p: 1.5, bgcolor: '#e3f2fd', borderBottom: '1px solid #bbdefb', display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                    <Typography variant="caption" sx={{ color: 'primary.dark', fontWeight: 'bold', mr: 1 }}>HILFE:</Typography>
                                    {getAvailableTags().map(tag => (
                                        <Button 
                                            key={tag} 
                                            size="small" 
                                            variant="outlined" 
                                            onClick={() => insertTag(tag)}
                                            sx={{ py: 0, px: 1, textTransform: 'none', fontSize: '0.7rem', height: 22 }}
                                        >
                                            {tag}
                                        </Button>
                                    ))}
                                    <Typography variant="caption" sx={{ ml: 'auto', color: 'primary.dark', fontWeight: 'bold' }}>
                                        Wichtig: {`{{content}}`} setzen!
                                    </Typography>
                                </Box>
                                <TextField
                                    multiline
                                    fullWidth
                                    variant="standard"
                                    value={localFrame}
                                    onChange={e => setLocalFrame(e.target.value)}
                                    placeholder="HTML-Rahmen hier eingeben..."
                                    sx={{ 
                                        p: 2,
                                        '& .MuiInputBase-root': { 
                                            fontFamily: 'monospace', 
                                            fontSize: '0.85rem'
                                        }
                                    }}
                                    InputProps={{ disableUnderline: true }}
                                />
                            </Box>
                        )}

                        {/* TAB: STYLE (Colors) */}
                        {editTab === 'style' && (
                            <Box sx={{ p: 3 }}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Farben & Akzente</Typography>
                                <Divider sx={{ mb: 3 }} />
                                <Grid container spacing={4}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>HINTERGRUND-FARBE</Typography>
                                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                            <input 
                                                type="color" 
                                                value={emailBgColor}
                                                onChange={e => setEmailBgColor(e.target.value)}
                                                style={{ width: 60, height: 40, padding: 0, border: '1px solid #ccc', cursor: 'pointer', borderRadius: 4 }}
                                            />
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#eee', px: 1, py: 0.5, borderRadius: 1 }}>{emailBgColor}</Typography>
                                        </Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                            Variable: <code>{`{{emailBackgroundColor}}`}</code>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>AKZENT-FARBE</Typography>
                                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                            <input 
                                                type="color" 
                                                value={emailAccentColor}
                                                onChange={e => setEmailAccentColor(e.target.value)}
                                                style={{ width: 60, height: 40, padding: 0, border: '1px solid #ccc', cursor: 'pointer', borderRadius: 4 }}
                                            />
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#eee', px: 1, py: 0.5, borderRadius: 1 }}>{emailAccentColor}</Typography>
                                        </Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                            Variable: <code>{`{{emailAccentColor}}`}</code>
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </Grid>

                    {/* Preview Side */}
                    {(!isMobile || activeView === 'preview') && (
                        <Grid item xs={12} lg={6} sx={{ bgcolor: '#f0f2f5', display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <Box sx={{ p: 1, borderBottom: '1px solid #ddd', bgcolor: 'white', display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="caption" fontWeight="bold" color="text.secondary">LIVE VORSCHAU</Typography>
                                <Typography variant="caption" color="text.secondary">{isMobile ? 'Mobile Ansicht' : '600px Breite'}</Typography>
                            </Box>
                            <Box sx={{ 
                                flexGrow: 1, 
                                p: { xs: 1.5, sm: 3 }, 
                                overflowY: 'auto', 
                                overflowX: 'hidden',
                                overflowAnchor: 'none',
                                WebkitOverflowScrolling: 'touch',
                                pb: 10
                            }}>
                                {/* Email Header Simulation */}
                                <Paper sx={{ width: '100%', maxWidth: 600, p: 2, bgcolor: 'white', mx: 'auto', mb: 2 }} elevation={1}>
                                    <Stack spacing={0.5}>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Typography variant="caption" fontWeight="bold" sx={{ width: 60 }}>Von:</Typography>
                                            <Typography variant="caption">TC Opfingen &lt;info@tc-opfingen.de&gt;</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Typography variant="caption" fontWeight="bold" sx={{ width: 60 }}>An:</Typography>
                                            <Typography variant="caption">max@example.com</Typography>
                                        </Box>
                                        <Divider sx={{ my: 1 }} />
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <Typography variant="body2" fontWeight="bold" sx={{ color: '#333' }}>
                                                {selectedTemplate?.subject || '(Kein Betreff)'}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>

                                {/* Email Body (The combined template) */}
                                <Paper 
                                    sx={{ 
                                        width: '100%', 
                                        maxWidth: 600, 
                                        mx: 'auto',
                                        height: 'fit-content',
                                        minHeight: 400,
                                        p: 0,
                                        overflow: 'hidden',
                                        bgcolor: 'white'
                                    }}
                                    elevation={3}
                                >
                                    <Box sx={{ 
                                        overflow: 'auto',
                                        '& img': { maxWidth: '100%' },
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                        '.ql-align-center': { textAlign: 'center' },
                                        '.ql-align-right': { textAlign: 'right' },
                                        '.ql-align-justify': { textAlign: 'justify' }
                                    }}>
                                        <div dangerouslySetInnerHTML={{ __html: getLocalRenderedTemplate() }} />
                                    </Box>
                                </Paper>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, bgcolor: '#f8f9fa', borderTop: '1px solid #ddd' }}>
                 <Stack direction="row" spacing={1} sx={{ width: '100%', alignItems: 'center' }}>
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                        <FaHistory size={12} />
                        <Typography variant="caption">Änderungen lokal gepuffert</Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button 
                        onClick={onHide} 
                        color="inherit" 
                        size={isMobile ? "small" : "medium"}
                        sx={{ borderRadius: 2, px: 2 }}
                    >
                        Abbrechen
                    </Button>
                    <Button 
                        variant="contained" 
                        color="primary"
                        startIcon={<MdSave />}
                        onClick={onSave}
                        size={isMobile ? "small" : "medium"}
                        sx={{ 
                            borderRadius: 2, 
                            px: isMobile ? 2 : 3,
                            boxShadow: '0 2px 8px rgba(25, 118, 210, 0.25)',
                            fontWeight: 'bold'
                        }}
                    >
                        {isMobile ? 'Speichern' : 'Design Speichern'}
                    </Button>
                 </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default EmailTemplateEditor;
